/**
 * Central API client.
 *
 * Deliberately thin: no data-fetching library, because every screen here is a
 * straightforward read against an API that already returns exactly what the
 * screen needs. The one piece of real logic is token handling.
 */
const TOKEN_KEY = 'netegate.central.token';
const STAFF_KEY = 'netegate.central.staff';

export interface Staff {
  id: string;
  email: string;
  fullName: string;
  role: 'operations' | 'finance' | 'support' | 'superadmin';
}

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    readonly details?: Record<string, unknown>,
  ) {
    super(message);
  }
}

export function getToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEY);
}

export function getStaff(): Staff | null {
  const raw = sessionStorage.getItem(STAFF_KEY);
  return raw ? (JSON.parse(raw) as Staff) : null;
}

export function setSession(token: string, staff: Staff): void {
  // sessionStorage, not localStorage: this console shows fleet-wide financial
  // and ownership data, so a session should not outlive the browser tab on a
  // shared machine.
  sessionStorage.setItem(TOKEN_KEY, token);
  sessionStorage.setItem(STAFF_KEY, JSON.stringify(staff));
}

export function clearSession(): void {
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(STAFF_KEY);
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  const response = await fetch(`/api/v1${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init.headers,
    },
  });

  if (response.status === 401) {
    clearSession();
    window.location.hash = '#/signin';
    throw new ApiError('unauthorized', 'Your session has expired', 401);
  }

  const body = response.status === 204 ? null : await response.json().catch(() => null);

  if (!response.ok) {
    const error = body as { code?: string; message?: string; details?: Record<string, unknown> } | null;
    throw new ApiError(
      error?.code ?? 'request_failed',
      error?.message ?? `Request failed with status ${response.status}`,
      response.status,
      error?.details,
    );
  }

  return body as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) }),

  async signIn(email: string, password: string) {
    const result = await request<{ accessToken: string; staff: Staff }>('/auth/staff/signin', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    setSession(result.accessToken, result.staff);
    return result.staff;
  },
};

/** Formats a money amount without pretending to know the locale's conventions. */
export function money(amount: number | string | null | undefined, currency = 'AED'): string {
  const value = Number(amount ?? 0);
  return `${currency} ${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function shortDate(value: string | null | undefined): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
}

export function relativeTime(value: string | null | undefined): string {
  if (!value) return 'never';
  const seconds = Math.floor((Date.now() - new Date(value).getTime()) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}
